import type { FC } from 'react';
import React from 'react';
import { Box, Button, Stack } from '@mui/material';
import {
  endOfDay,
  endOfMonth,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DateRange } from './DateRange';

const PRESETS = [
  {
    key: 'last-month',
    from: (now: Date) => startOfMonth(subMonths(now, 1)),
    to: (now: Date) => endOfMonth(subMonths(now, 1)),
  },
  { key: 'this-month', from: startOfMonth, to: endOfDay },
  { key: 'this-quarter', from: startOfQuarter, to: endOfDay },
  { key: 'year-to-date', from: startOfYear, to: endOfDay },
] as const;

interface DateRangeFilterProps {
  range?: { from?: Date; to?: Date };
  onChange: (range: { from?: Date; to?: Date }) => void;
}

// The pill editor for a date range: quick presets above an inline calendar.
export const DateRangeFilter: FC<DateRangeFilterProps> = ({
  range,
  onChange,
}) => {
  const { t } = useTranslation();
  const now = new Date();
  return (
    <Stack
      sx={{
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {PRESETS.map((preset) => {
          const from = startOfDay(preset.from(now));
          const to = preset.to(now);
          const active =
            range?.from !== undefined &&
            range.to !== undefined &&
            isSameDay(range.from, from) &&
            isSameDay(range.to, to);
          return (
            <Button
              key={preset.key}
              size="small"
              variant={active ? 'contained' : 'outlined'}
              color={active ? 'primary' : 'inherit'}
              onClick={() => onChange({ from, to })}
            >
              {t(`table.filters.date-range.preset.${preset.key}`)}
            </Button>
          );
        })}
      </Box>
      <DateRange inline range={range} onChange={onChange} />
    </Stack>
  );
};
